import { createMountsContainerNode, createNodeRuleSet, createPadNodeUnderMountsContainer, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, FootPrintPadHoleType, FootPrintPadShape, TextAlignment, } from "@buildwithflux/models";
import { metersToMm } from "@buildwithflux/shared";
// eslint-disable-next-line boundaries/element-types
import { PcbLayoutRuleCompiler } from "../../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { TextPlaceholder } from "../../SharedDataModels";
import { replaceAutoWithDefault2, replaceAutoWithDefault3 } from "../utils";
import { designatorFontSize, designatorMargin, epadString, pinMarkerMargin, pinMarkerRadius, silkWidth, } from "./commonConstants";
function getSide(i, gridX, gridY) {
    if (i < gridX) {
        // left side
        return 0;
    }
    else if (i < gridX + gridY) {
        // bottom side
        return 1;
    }
    else if (i < gridX * 2 + gridY) {
        // right side
        return 2;
    }
    else if (i < gridX * 2 + gridY * 2) {
        // top side
        return 3;
    }
    return -1;
}
function qfnInferPinLayout(bakedRules, existingPadNodes) {
    const result = { grid: [], otherPads: [] };
    // We support "provisional" ordering using the pad designator
    const withoutPinNumberPadNodes = existingPadNodes.filter((pad) => pad.terminalId === "" || pad.terminalId === undefined);
    withoutPinNumberPadNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    // We want to know which pads are left in the end
    const remainingPadsWithNumbers = existingPadNodes.filter((pad) => pad.terminalId !== "" && pad.terminalId !== undefined);
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const gridY = bakedRules.gridArrayCount?.y ?? 0;
    const total = (gridX + gridY) * 2;
    for (let i = 0; i < total; i++) {
        // This makes the loop O(n^2), but n is usually small enough and decreases every iteration, check in the future if it becomes a problem
        const foundMatchingI = remainingPadsWithNumbers.findIndex((pad) => pad.terminalId === `${i + 1}`);
        const foundMatching = remainingPadsWithNumbers[foundMatchingI];
        if (foundMatchingI !== -1) {
            remainingPadsWithNumbers.splice(foundMatchingI, 1);
        }
        // We get a pad from the unnumbered ones if we don't have any in that position
        const matchingPad = foundMatching ?? withoutPinNumberPadNodes.shift();
        const sideId = getSide(i, gridX, gridY);
        if (matchingPad && sideId === -1) {
            result.otherPads.push(matchingPad);
        }
        else {
            const sideArr = result.grid[sideId] ?? [];
            sideArr.push(matchingPad ?? null);
            result.grid[sideId] = sideArr;
        }
    }
    result.otherPads.push(...withoutPinNumberPadNodes);
    result.otherPads.push(...remainingPadsWithNumbers);
    return result;
}
export function qfnComputeInferredRules(bakedRules, config, existingPadNodes) {
    const pinSize = replaceAutoWithDefault2(bakedRules.pinSize, { x: config.pinSizeX, y: config.pinSizeY });
    const pinPitch = replaceAutoWithDefault2(bakedRules.pinPitch, { x: config.pinPitch, y: config.pinPitch });
    const padOuterMargin = replaceAutoWithDefault2(bakedRules.padOuterMargin, {
        x: config.padOuterMargin,
        y: config.padOuterMargin,
    });
    const pinsMinusEpad = existingPadNodes.filter((pad) => pad.terminalId !== epadString);
    const padCountX = Math.ceil(pinsMinusEpad.length / 4);
    const padCountY = Math.floor(pinsMinusEpad.length / 4);
    const gridArrayCount = replaceAutoWithDefault2(bakedRules.gridArrayCount, {
        x: config.gridArrayCountX ?? padCountX,
        y: config.gridArrayCountY ?? padCountY,
    });
    const packageSize = replaceAutoWithDefault3(bakedRules.packageSize, {
        // Heuristic that seems to be working as a starting value, but still probably requires the user to adjust it
        x: gridArrayCount.x * pinPitch.x + pinSize.y * 2,
        y: gridArrayCount.y * pinPitch.y + pinSize.y * 2,
        z: config.packageHeight,
    });
    const newBakedRules = {
        ...bakedRules,
        packageSize,
        pinSize,
        pinPitch,
        gridArrayCount,
        padOuterMargin,
    };
    return { ...newBakedRules, pinLayout: qfnInferPinLayout(newBakedRules, existingPadNodes) };
}
export function qfnGeneratePadRules(footprintNodeUid, bakedRules) {
    const result = {};
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const gridY = bakedRules.gridArrayCount?.y ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const pinPitchY = bakedRules.pinPitch?.y ?? 0;
    const padOuterMargin = bakedRules.padOuterMargin?.x ?? 0;
    const packageSizeX = bakedRules.packageSize?.x ?? 0;
    const packageSizeY = bakedRules.packageSize?.y ?? 0;
    const totalX = (gridX - 1) * pinPitchX;
    const totalY = (gridY - 1) * pinPitchY;
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return result; // should never happen
    for (let sideId = 0; sideId < pinLayout.grid.length; sideId++) {
        const sideArr = pinLayout.grid[sideId];
        if (!sideArr)
            continue;
        for (let i = 0; i < sideArr.length; i++) {
            const matchingPad = sideArr[i];
            // Can happen if a pin is not present, which is intentional as it's the only way to leave a hole in the footprint
            if (!matchingPad)
                continue;
            let posX = 0;
            let posY = 0;
            let rotZ = 0;
            if (sideId === 0) {
                // left side
                posX = -(packageSizeX / 2 - pinSizeY / 2 + padOuterMargin / 2);
                posY = -(i * pinPitchY - totalY / 2);
                rotZ = 270;
            }
            else if (sideId === 1) {
                // bottom side
                posX = i * pinPitchX - totalX / 2;
                posY = -(packageSizeY / 2 - pinSizeY / 2 + padOuterMargin / 2);
                rotZ = 180;
            }
            else if (sideId === 2) {
                // right side
                posX = packageSizeX / 2 - pinSizeY / 2 + padOuterMargin / 2;
                posY = i * pinPitchY - totalY / 2;
                rotZ = 90;
            }
            else if (sideId === 3) {
                // top side
                posX = -(i * pinPitchX - totalX / 2);
                posY = packageSizeY / 2 - pinSizeY / 2 + padOuterMargin / 2;
            }
            const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPad.type, matchingPad.uid);
            const ruleSet = {
                // NOTE: the ruleset ID and the node ID is assumed to be the same elsewhere.
                // See PcbSelectorMatcher.
                uid: matchingPad.uid,
                selector: selector,
                rules: {},
                generatedFromAncestorNodeUid: footprintNodeUid,
            };
            result[ruleSet.uid] = ruleSet;
            const rules = {
                position: `${metersToMm(posX)}mm ${metersToMm(posY)}mm`,
                size: `${metersToMm(pinSizeX)}mm ${metersToMm(pinSizeY + padOuterMargin)}mm`,
                rotationZ: `${rotZ}deg`,
            };
            ruleSet.rules = createNodeRuleSet(rules);
        }
    }
    const matchingEPad = pinLayout.otherPads.find((pad) => pad.terminalId === epadString);
    if (matchingEPad && bakedRules.thermalPadSize) {
        const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingEPad.type, matchingEPad.uid);
        const ruleSet = {
            // NOTE: the ruleset ID and the node ID is assumed to be the same elsewhere.
            // See PcbSelectorMatcher.
            uid: matchingEPad.uid,
            selector: selector,
            rules: {},
            generatedFromAncestorNodeUid: footprintNodeUid,
        };
        result[ruleSet.uid] = ruleSet;
        const rules = {
            position: `0 0`,
            padShape: FootPrintPadShape.rectangle,
            padHoleType: FootPrintPadHoleType.SurfaceMountDevice,
            size: `${metersToMm(bakedRules.thermalPadSize.x)}mm ${metersToMm(bakedRules.thermalPadSize.y)}mm`,
        };
        ruleSet.rules = createNodeRuleSet(rules);
    }
    return result;
}
export function qfnGenerateAdditionalPads(footprintNodeUid, bakedRules) {
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return [];
    const existing = pinLayout.otherPads.find((pad) => pad.terminalId === epadString);
    if (!existing && bakedRules.thermalPadSize && bakedRules.thermalPadSize.x > 0 && bakedRules.thermalPadSize.y > 0) {
        const mountsContainerNode = createMountsContainerNode(footprintNodeUid);
        const padNode = createPadNodeUnderMountsContainer(footprintNodeUid + "_epad", "Thermal Pad", mountsContainerNode.uid, footprintNodeUid);
        padNode.pcbNodeRuleSet = createNodeRuleSet({
            position: `0 0`,
            padShape: FootPrintPadShape.rectangle,
            padHoleType: FootPrintPadHoleType.SurfaceMountDevice,
            size: `${metersToMm(bakedRules.thermalPadSize.x)}mm ${metersToMm(bakedRules.thermalPadSize.y)}mm`,
        }, true);
        return [mountsContainerNode, padNode];
    }
    return [];
}
export function qfnGenerateSilkNodes(footprintNodeUid, bakedRules) {
    if (!bakedRules.packageSize)
        return [];
    const gridY = bakedRules.gridArrayCount?.y ?? 0;
    const pinPitchY = bakedRules.pinPitch?.y ?? 0;
    const totalY = (gridY - 1) * pinPitchY;
    const padOuterMargin = bakedRules.padOuterMargin?.x ?? 0;
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    const packageWidth = bakedRules.packageSize.x;
    const packageHeight = bakedRules.packageSize.y;
    // outer shell
    silkPaths.push(`<path d="
              M ${-packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${packageHeight / 2}
              L ${-packageWidth / 2} ${packageHeight / 2}
              Z" stroke-width="${silkWidth}"
              stroke-linecap="round" fill="none" />`);
    // pin 1
    const pin1Y = -totalY / 2;
    silkPaths.push(`<circle
              cx="${-packageWidth / 2 - pinMarkerMargin - padOuterMargin}"
              cy="${pin1Y}"
              r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    // Designator text
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(0)}mm ${metersToMm(packageHeight / 2 + designatorMargin + padOuterMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
//# sourceMappingURL=qfn.js.map